import {
  Calculator,
  CheckCircle2,
  FileUp,
  GitBranch,
  Lock,
  Route,
  Sigma,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

/**
 * The monthly close, step by step, plus the vendor rules the mapping layer
 * encodes. Static prose — this is the COGS handoff doc's accumulated knowledge
 * living next to the workbench it now drives.
 */
export function HowToView() {
  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <Card className="p-5">
        <h2 className="text-lg font-semibold">What this section is</h2>
        <p className="mt-1.5 text-sm text-muted-foreground">
          The monthly COGS-per-unit close. Each month we take every vendor invoice behind
          the product (AWS, Twilio, SendGrid, Motor, the mail house…), route each charge
          to the product line it serves, and produce the number that feeds margin
          reporting: <strong>$/metric per product line</strong> — cost per email sent, per
          text credit, per mailer piece, per location. It replaces the hand-built 10-sheet
          Excel cost report.
        </p>
      </Card>

      <Step
        icon={<FileUp className="h-4 w-4" />}
        step="Step 1"
        title="Upload the month's cost files (Uploads tab)"
        points={[
          "Upload the monthly Cost Report workbook (and/or the mailer costs file). Every cost row lands as a line item; the Simple Summary sheet also seeds the metric quantities.",
          "Uploading auto-opens the month. Re-uploading a file with the same name replaces its rows — a corrected workbook just fixes the month.",
          "Each file's ingestion status, row count, and parser warnings are tracked per upload.",
        ]}
      />
      <Step
        icon={<Route className="h-4 w-4" />}
        step="Step 2"
        title="Map what's new (Mappings tab)"
        points={[
          "Most rows map themselves — durable rules route recurring charges (the seed rules encode the handoff doc). Only genuinely new charges show up as unmapped.",
          "Classifying a line creates the rule that catches the same charge next month: vendor, match pattern, product line, and treatment (amortization, credit, excluded).",
          "Every rule change is recorded in the append-only changelog with who and why.",
        ]}
      />
      <Step
        icon={<Sigma className="h-4 w-4" />}
        step="Step 3"
        title="Check the denominators (Calculations tab)"
        points={[
          "$/metric needs a quantity: emails sent, text credits, mailer pieces, locations. Workbook-parsed values show as “workbook”; type a value to override (manual always wins).",
          "Text credits = SMS segments + 2 × MMS segments — pull segment counts from the Twilio console dashboard, not the CSV.",
          "Capacity methods per line: actual ($ ÷ units), target rate (Voice $0.07/min, OEM $4.25/loc — utilization = units ÷ capacity), marginal (Text: strip the prorated support/tax overhead for the excl-tax rate).",
        ]}
      />
      <Step
        icon={<Calculator className="h-4 w-4" />}
        step="Step 4"
        title="Review the month (Details tab)"
        points={[
          "The Details table is the Simple Summary: total expense, metric qty, $/metric, full-capacity $/metric excl/incl tax, and utilization per product line.",
          "Click any row to see the invoice lines behind the number; expand the drawer for the full mapped detail.",
          "A workbook tie-out flags any line where the computed total drifts from what the uploaded workbook said.",
        ]}
      />
      <Step
        icon={<Lock className="h-4 w-4" />}
        step="Step 5"
        title="Sign off"
        points={[
          "Sign-off freezes the month's outputs as a snapshot — the numbers downstream features read (per-customer profitability, margin tracking).",
          "A signed-off month rejects uploads, mapping edits, and metric changes until an approver reopens it.",
        ]}
      />

      <Card className="p-5">
        <div className="flex items-center gap-2">
          <GitBranch className="h-4 w-4 text-brand" />
          <h3 className="font-semibold">Vendor rules the mappings encode</h3>
        </div>
        <ul className="mt-3 flex flex-col gap-2 text-sm text-muted-foreground">
          <Rule label="Twilio bills in arrears">
            The invoice dated ~1st of a month covers the prior month&apos;s usage. Support +
            taxes arrive as single lines and prorate between Text and Voice by cost base —
            the computed-split rule does this automatically.
          </Rule>
          <Rule label="Amortizations">
            Zoho, Aurgima, and MUI X Pro annuals recognize at 1/12 per month; the two Motor
            contracts (MSA and Campaign Connect) recognize quarterly at 1/3.
          </Rule>
          <Rule label="Credits subtract">
            AWS support credit memos and the Snowflake Collaboration Rebate are credits —
            recognized in the month issued (cash basis).
          </Rule>
          <Rule label="Excluded from COGS">
            The ElevenLabs Success Package ($1,500/mo) is R&D/sales cost, not COGS — it is
            mapped but excluded, so it stays visible without moving the number.
          </Rule>
          <Rule label="Mailer batches">
            11×6 batches are identified by <code>-116-</code> (with hyphens) in the batch id
            — a bare “116” also matches unrelated batch numbers. Pieces (qty) ride on the
            postage row so they aren&apos;t double-counted.
          </Rule>
          <Rule label="SendGrid overage arrears">
            The Premier 20M plan bills overages one month behind — the May invoice carries
            April&apos;s overage. Same idea for Topol data transfer (invoice-date basis).
          </Rule>
        </ul>
      </Card>

      <Card className="p-5">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-brand" />
          <h3 className="font-semibold">Who can do what</h3>
        </div>
        <ul className="mt-3 flex flex-col gap-1.5 text-sm text-muted-foreground">
          <li>
            <Badge variant="secondary">cos_units.read</Badge> — Summary and Details (the
            numbers, the trends, the drill-down).
          </li>
          <li>
            <Badge variant="secondary">cos_units.write</Badge> — everything else: uploads,
            metric edits, calculations, and this page.
          </li>
          <li>
            <Badge variant="secondary">cos_units.map</Badge> — classify line items and edit
            mapping rules (implied by write).
          </li>
          <li>
            <Badge variant="secondary">cos_units.signoff</Badge> — sign off and reopen a
            monthly close.
          </li>
        </ul>
      </Card>
    </div>
  );
}

function Step({
  icon,
  step,
  title,
  points,
}: {
  icon: React.ReactNode;
  step: string;
  title: string;
  points: string[];
}) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-brand-light text-brand">
          {icon}
        </span>
        <Badge variant="secondary">{step}</Badge>
        <h3 className="font-semibold">{title}</h3>
      </div>
      <ul className="mt-3 flex list-disc flex-col gap-1.5 pl-10 text-sm text-muted-foreground">
        {points.map((p, i) => (
          <li key={i}>{p}</li>
        ))}
      </ul>
    </Card>
  );
}

function Rule({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <li>
      <span className="font-medium text-foreground">{label}.</span> {children}
    </li>
  );
}
